import { type Options, type PointOptionsObject, type SeriesLineOptions } from 'highcharts';
import { useEffect } from 'react';

import { COLOR_GRAY_100, COLOR_GRAY_700 } from '@/styles/palette';

import { useFlareContext } from '../FlareContext';
import LineLoader from '../loaders/LineLoader';
import {
  type FlareChart,
  type FlareDataPointOptions,
  type FlareSeriesOptions,
  type SanitizedFlareProps,
} from '../types';
import { getSelectionLine } from '../utils/mark-util';

type LineProps = {
  data?: FlareSeriesOptions[];
  colors?: string[];
  x?: string;
  y?: string;
  hideLoader?: boolean;
};

const buildLineOptions = (
  options: Options,
  parentProps: SanitizedFlareProps,
  props: LineProps,
): Options => {
  const { data: chartData, colors: chartColors, parseX, parseY } = parentProps;
  const { data: markData, x = 'x', y = 'y', colors: markColors } = props;
  const data = markData ?? chartData;
  const colors = markColors ?? chartColors;
  const series: SeriesLineOptions[] | undefined = data?.map(
    (series: FlareSeriesOptions, seriesIndex: number) => {
      const { name, data: seriesData, ...rest } = series;

      const newSeriesData = seriesData.map((item: FlareDataPointOptions) => {
        const newItem: PointOptionsObject = {
          ...item,
          x: parseX(item[x]),
          y: parseY(item[y]),
          colorIndex: seriesIndex,
        };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newItem[x as keyof PointOptionsObject];
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newItem[y as keyof PointOptionsObject];

        return newItem;
      });

      return {
        type: 'line',
        ...rest,
        name,
        color: colors[seriesIndex],
        lineColor: colors[seriesIndex],
        fillOpacity: 0.25,
        data: newSeriesData,
        states: {
          inactive: {
            opacity: 1,
          },
        },
        marker: {
          enabled: false,
          radius: 0.1,
          fillOpacity: 1,
          fillColor: colors[seriesIndex],
          states: {
            hover: {
              enabled: true,
              fillColor: undefined,
              lineColor: COLOR_GRAY_100,
              lineWidth: 1,
              radius: 5,
              animation: {
                duration: 200,
              },
            },
            select: {
              enabled: true,
              fillColor: undefined,
              lineColor: COLOR_GRAY_100,
              lineWidth: 1,
              radius: 8,
              animation: {
                duration: 200,
              },
            },
            normal: {
              animation: {
                duration: 200,
              },
            },
          },
        },
      };
    },
  );

  return {
    ...options,
    chart: {
      ...options.chart,
      type: 'line',
    },
    series: options.series ? options.series.concat(series ?? []) : series,
    plotOptions: {
      ...options.plotOptions,
      series: {
        ...options.plotOptions?.series,
        point: {
          ...options.plotOptions?.series?.point,
          events: {
            ...options.plotOptions?.series?.point?.events,

            select: function () {
              setTimeout(() => {
                const chart = this.series.chart as FlareChart;

                if (
                  chart.getSelectedPoints().length &&
                  chart.selectedBackground?.xValue !== this.x
                ) {
                  const line = chart.renderer
                    .path(getSelectionLine(chart))
                    .addClass('stroke-2 [stroke-dasharray:8_6]')
                    .attr({
                      stroke: COLOR_GRAY_700,
                    })
                    .add();

                  chart.selectedBackground = {
                    xValue: this.x,
                    graphic: line,
                  };
                }
              }, 0);
            },
            unselect: function () {
              const chart = this.series.chart as FlareChart;

              const { graphic: rect } = chart?.selectedBackground ?? {};
              if (rect) {
                rect.destroy();
              }
              chart.selectedBackground = undefined;
            },
          },
        },
      },
      line: {
        marker: {
          enabled: true,
          symbol: 'circle',
        },
      },
    },
  };
};

const Line = (props: LineProps) => {
  const { id, registerChild, isLoading } = useFlareContext();

  useEffect(() => {
    registerChild(id, (options: Options, parentProps: SanitizedFlareProps) =>
      buildLineOptions(options, parentProps, props),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  if (isLoading && !props.hideLoader) {
    return <LineLoader style={{ flex: 1 }} />;
  }

  return null;
};

export default Line;
